import { Component, Element, h, Host, Listen, Prop } from "@stencil/core";

import { StzhDisturberCloseEvent, StzhDisturberOpenEvent } from "../../../index";

import { hasSlot, setPropsIfNull } from "../../../utils/utils";
import { media } from "../../../utils/media-utils";

/**
 * @slot heading - Slot for `stzh-heading` element
 * @slot image - Slot for `stzh-figure` or `img` element
 * @slot lead - Slot for `stzh-text` element
 * @slot tag - Slot for `stzh-tag` element
 * @slot readspeaker - Slot for `stzh-readspeaker` element
 * @slot disturber - Slot for disturber content
 */
@Component({
  tag: "stzh-pagetitle",
  styleUrl: "stzh-pagetitle.scss",
  scoped: true,
})
export class StzhPagetitle {
  @Element() element: HTMLStzhPagetitleElement;

  /** Background color */
  @Prop({ reflect: true }) background: "default" | "transparent" = "default";

  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  /** Dateline */
  @Prop() dateline: string;

  @Listen("resize", { target: "window" })
  handleResize() {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.update);
  }

  @Listen("stzhOpen")
  handleStzhOpen(event: CustomEvent<StzhDisturberOpenEvent>) {
    if (event.detail.component === "stzh-disturber") {
      this.isDisturberOpen = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(this.update);
      });
    }
  }

  @Listen("stzhClose")
  handleStzhClose(event: CustomEvent<StzhDisturberCloseEvent>) {
    if (event.detail.component === "stzh-disturber") {
      this.isDisturberOpen = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(this.update);
      });
    }
  }

  private observer: MutationObserver;
  private debounceResize: number;
  private disturberElement: HTMLDivElement;
  private isDisturberOpen: boolean = false;

  private update = () => {
    if (!this.disturberElement) {
      return;
    }

    const isMedium = media("medium").matches;
    const disturberHeight = this.disturberElement?.offsetHeight || 0;
    const hasDisturber = disturberHeight && this.isDisturberOpen;
    const isStuck = hasDisturber && !isMedium;

    document.documentElement.style.setProperty(
      "--stzh-pagetitle-disturber-height",
      hasDisturber ? `${disturberHeight}px` : "0px",
    );
    document.documentElement.style.setProperty(
      "--stzh-pagetitle-disturber-stuck-height",
      isStuck ? `${disturberHeight}px` : "0px",
    );
    document.documentElement.style.setProperty("--stzh-pagetitle-disturber-is-stuck", isStuck ? "1" : "0");
    document.documentElement.style.setProperty("--stzh-pagetitle-disturber-is-not-stuck", isStuck ? "0" : "1");

    // document.documentElement.classList.toggle("stzh-pagetitle-disturber-is-stuck", isStuck);
  };

  private init = () => {
    const heading = this.element.querySelector('stzh-heading[slot="heading"], [slot="heading"] stzh-heading');
    const disturber = this.element.querySelector('stzh-disturber[slot="disturber"], [slot="disturber"] stzh-disturber');
    const leads = this.element.querySelectorAll('stzh-text[slot="lead"], [slot="lead"] stzh-text');
    const show = this.element.querySelector('stzh-show[slot="disturber"], [slot="disturber"] stzh-show');
    const readspeakerButton = this.element.querySelector('[slot="readspeaker"] stzh-button');
    const tag = this.element.querySelector('stzh-tag[slot="tag"], [slot="tag"] stzh-tag');

    if (heading) {
      setPropsIfNull(heading, {
        curve: "hero",
        level: "1",
      } as HTMLStzhHeadingElement);
    }

    if (disturber) {
      setPropsIfNull(disturber, {
        initialOpenAnimation: true,
      } as HTMLStzhDisturberElement);
    }

    if (show) {
      setPropsIfNull(show, {
        mikro: true,
      } as HTMLStzhShowElement);
    }

    if (tag) {
      setPropsIfNull(tag, {
        type: "default",
        size: "large",
        nonInteractive: true,
      } as HTMLStzhTagElement);
    }

    if (readspeakerButton) {
      setPropsIfNull(readspeakerButton, {
        variant: "tertiary-plain",
        noPaddingLeft: true,
        noPaddingRight: true,
        size: "small",
        sizeMedium: "tiny",
        buttonAccesskey: "L",
      } as HTMLStzhButtonElement);
    }

    leads.forEach(lead => {
      setPropsIfNull(lead, {
        lead: true,
      } as HTMLStzhTextElement);
    });
  };

  componentDidRender() {
    requestAnimationFrame(() => {
      requestAnimationFrame(this.update);
    });
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  render() {
    const imageSlotUsed = hasSlot(this.element, "image");
    const leadSlotUsed = hasSlot(this.element, "lead");
    const tagSlotUsed = hasSlot(this.element, "tag");
    const readspeakerSlotUsed = hasSlot(this.element, "readspeaker");
    const disturberElement = this.element.querySelector(
      'stzh-disturber[slot="disturber"], [slot="disturber"] stzh-disturber',
    );

    const classes = {
      "stzh-pagetitle": true,
      "stzh-pagetitle--has-image": imageSlotUsed,
      "stzh-pagetitle--has-no-image": !imageSlotUsed,
      "stzh-pagetitle--has-lead": leadSlotUsed,
      "stzh-pagetitle--has-tag": tagSlotUsed,
      "stzh-pagetitle--has-dateline": !!this.dateline,
      "stzh-pagetitle--has-meta": tagSlotUsed || readspeakerSlotUsed,
      "stzh-pagetitle--has-disturber": !!disturberElement,
      "stzh-pagetitle--hyphens": this.hyphensEnabled,
      [`stzh-pagetitle--background-${this.background}`]: !!this.background,
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-pagetitle__container">
            <div class="stzh-pagetitle__wrapper">
              <div class="stzh-pagetitle__text">
                <div class="stzh-pagetitle__meta">
                  <div class="stzh-pagetitle__tag">
                    <slot name="tag"></slot>
                  </div>

                  <div class="stzh-pagetitle__readspeaker">
                    <slot name="readspeaker"></slot>
                  </div>
                </div>

                {this.dateline && <div class="stzh-pagetitle__dateline">{this.dateline}</div>}

                <div class="stzh-pagetitle__heading">
                  <slot name="heading"></slot>
                </div>

                <div class="stzh-pagetitle__lead">
                  <slot name="lead"></slot>
                </div>
              </div>

              <div class="stzh-pagetitle__image-wrapper">
                <stzh-ratio
                  ratio={"16:9"}
                  ratioSmall={"3:2"}
                  ratioMedium={"16:9"}
                  class="stzh-pagetitle__ratio"
                ></stzh-ratio>
                <div class="stzh-pagetitle__image-inner-wrapper">
                  <slot name="image"></slot>
                </div>
              </div>

              <div class="stzh-pagetitle__disturber" ref={el => (this.disturberElement = el as HTMLDivElement)}>
                <slot name="disturber"></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
